import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  useTheme,
  ListItemIcon,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { isEmpty, sortBy } from 'lodash';
import Loader from '../../features/Loader';
import { networksActions } from '../../redux/networksSlice';
import { FORM, STRINGS } from '../../constants';
import { Error } from '../../features/ErrorBoundary';
import { WE_HAVE_A_LITTLE_PROBLEM } from '../../features/ErrorBoundary/constants';
import { SELECT } from '../../constants/strings';
import { getValue } from '../../utils/object';
import { CHOOSE_FROM, NETWORK_MARKETING_DETAILS } from './constants';

const ServicePricesModal = ({
  open,
  onClose,
  onSubmit,
  values = {},
  isInternational,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [isError, setIsError] = useState(false);

  const loadNetworkPrices = useCallback(async () => {
    const postcode = getValue(
      values,
      FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
      ''
    );
    const countryCode = getValue(
      values,
      FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY,
      STRINGS.GB
    );

    const quickQuoteNetworks = await dispatch(
      networksActions.fetchServicePrices({
        weight: values.weight || 0,
        countryCode,
        ...(!isEmpty(postcode) ? { postcode } : {}),
      })
    ).unwrap();

    if (!quickQuoteNetworks?.length) {
      setIsError(true);

      throw new Error(STRINGS.NO_NETWORKS_ERROR);
    }

    return sortBy(quickQuoteNetworks, ['servicePrice']);
  }, [dispatch, values]);

  useEffect(() => {
    if (open) {
      setIsError(false);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      aria-labelledby='service-prices-modal-title'
      data-testid='service-prices-modal'
    >
      <Box
        sx={{
          position: 'absolute',
          overflow: 'hidden',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          maxWidth: '90vw',
          maxHeight: '90vh',
          minHeight: 'fit-content',
        }}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={2}
        >
          <Typography
            id='service-prices-modal-title'
            variant='h2'
            component='h2'
            color='primary.main'
          >
            {!isError && CHOOSE_FROM}
          </Typography>
          <IconButton onClick={onClose} data-testid='close-modal'>
            <CloseIcon />
          </IconButton>
        </Box>
        <Loader
          promiseFn={loadNetworkPrices}
          errorComponent={() => {
            setIsError(true);

            return (
              <Error
                title={WE_HAVE_A_LITTLE_PROBLEM}
                descriptionArray={[STRINGS.NO_NETWORKS_ERROR]}
              />
            );
          }}
        >
          {(networks = []) => (
            <List sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 88px)' }}>
              {networks.map((network, index) => {
                const config =
                  NETWORK_MARKETING_DETAILS(theme)[network.networkCode];
                const Icon = config.Icon;

                return (
                  <ListItem
                    key={index}
                    sx={{
                      mb: 4,
                      backgroundColor: theme.palette.primary.pageBackground,
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      p: 3,
                      overflow: 'hidden',
                      ...(index === networks.length - 1 && {
                        mb: 1,
                      }),
                    }}
                    data-testid={`service[${index}]`}
                  >
                    <Box
                      sx={{
                        backgroundColor: config.bannerColor,
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        transform: 'translate(22%, 110%) rotate(45deg)',
                        p: 1,
                        width: '200px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.contrastText,
                        }}
                      >
                        {config.bannerText}
                      </Typography>
                    </Box>
                    <Icon sx={{ mb: 1, width: '37px', height: '37px' }} />
                    <Typography
                      variant='h5'
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        maxWidth: { xs: '185px', sm: '230px' },
                      }}
                    >
                      {network.networkDesc}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ mb: 1, maxWidth: '250px' }}
                    >
                      {config.description}
                    </Typography>
                    <List sx={{ p: 0 }}>
                      {(config.sellingPoints || []).map(
                        ({ Icon, text }, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                              pl: 0,
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: '42px' }}>
                              <Icon sx={{ width: '30px', height: '30px' }} />
                            </ListItemIcon>
                            <ListItemText>{text}</ListItemText>
                          </ListItem>
                        )
                      )}
                    </List>
                    <Box
                      sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}
                    >
                      <Typography variant='h5' sx={{ fontWeight: 600 }}>
                        {`£${Number(network.servicePrice || 0).toFixed(2)}`}
                      </Typography>
                      {!isInternational && (
                        <Typography variant='body2' sx={{ ml: 1 }}>
                          {' '}
                          excl VAT
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={() => onSubmit(network.networkCode)}
                      fullWidth
                    >
                      {SELECT}
                    </Button>
                    <Typography variant='body2' sx={{ mt: 2 }}>
                      {config.deliveryTiming}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Loader>
      </Box>
    </Modal>
  );
};

ServicePricesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  isInternational: PropTypes.bool,
};

export default ServicePricesModal;
